import { Extension, type CommandProps } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const INDENT_STEP_PX = 24;
const MAX_INDENT_LEVEL = 6;

export const Indent = Extension.create({
  name: 'indent',

  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indentLevel: {
            default: 0,
            parseHTML: (element) => {
              const marginLeft = parseInt(element.style.marginLeft || '0', 10);
              return marginLeft > 0 ? Math.round(marginLeft / INDENT_STEP_PX) : 0;
            },
            renderHTML: (attributes) => {
              const level = attributes.indentLevel ?? 0;
              if (!level) return {};
              return { style: `margin-left: ${level * INDENT_STEP_PX}px` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    // Each block of the selection moves from its own level (a select-all starts in the document node).
    const shift =
      (delta: number) =>
      ({ state, tr }: CommandProps) => {
        const { types } = this.options;
        state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
          if (!types.includes(node.type.name)) return;
          const level = Math.min(Math.max(node.attrs.indentLevel + delta, 0), MAX_INDENT_LEVEL);
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indentLevel: level });
        });
        return true;
      };

    return {
      indent: () => shift(1),
      outdent: () => shift(-1),
    };
  },
});
